import { debounce } from 'lodash-es';
import { useEffect, useMemo, useRef, useState } from 'react';
import { filter, fromEvent, map, tap } from 'rxjs';

import { useSubscriptions } from '@/hook';
import { CustomEvent, isButtonElement, isInputElement } from '@/util';

import {
  DIALOG_CLASSNAME,
  DIALOG_EVENT_TYPE,
  DIALOG_TRIGGER,
} from './constant';
import type { VDialogEmitFunction, VDialogProps } from './type';
import { zIndexFactory } from './util';

type UseDialogParameters = {
  props: VDialogProps;
  emit: VDialogEmitFunction;
};

export default function useDialog({ props, emit }: UseDialogParameters) {
  const { visible, duration, noCloseOnEsc, initialZIndex } = props;
  const [innerVisible, setInnerVisible] = useState(false);
  const [zIndex, setZIndex] = useState(-1);
  const [hideTrigger, setHideTrigger] = useState<Nullish<DIALOG_TRIGGER>>(null);
  const escKeyEventSubscription = useMemo(
    () =>
      fromEvent<KeyboardEvent>(window, 'keyup')
        .pipe(
          map(e => [e.key, e.target] as [string, HTMLElement]),
          filter(
            ([, target]) => !isInputElement(target) && !isButtonElement(target),
          ),
          filter(([key]) => key === 'Escape' && visible && !noCloseOnEsc),
          filter(
            ([, target]) =>
              target === document.body ||
              target.classList.contains(DIALOG_CLASSNAME.CONTAINER) ||
              target.closest(`.${DIALOG_CLASSNAME.CONTAINER}`) ===
                containerRef.current,
          ),
          tap(() => {
            setHideTrigger(DIALOG_TRIGGER.ESC);
            hide();
          }),
        )
        .subscribe(),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [visible],
  );
  const [prevActiveElement, setPrevActiveElement] =
    useState<Nullish<HTMLElement>>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const zIndexManager = useRef(zIndexFactory(initialZIndex)).current;

  const afterChangeVisible = (newInnerVisible: boolean) => {
    newInnerVisible ? afterShow() : afterHide();
  };

  const afterDuration = (handler: () => any) => {
    debounce(handler, duration)();
  };

  const afterHide = () => {
    prevActiveElement?.focus();

    afterDuration(() => {
      emitWithEvent(DIALOG_EVENT_TYPE.HIDDEN);
    });
  };

  const afterShow = () => {
    setHideTrigger(null);
    setZIndex(zIndexManager.get());

    afterDuration(() => {
      focusContainer();
      emitWithEvent(DIALOG_EVENT_TYPE.SHOWN);
    });
  };

  const beforeChangeVisible = async (
    newVisible: boolean,
    beforeVisible: boolean,
  ): Promise<{
    isChanged: boolean;
    newInnerVisible: boolean;
  }> => {
    const type = newVisible ? DIALOG_EVENT_TYPE.SHOW : DIALOG_EVENT_TYPE.HIDE;
    const defaultPrevented = await emitWithEvent(type, hideTrigger);

    if (newVisible) setPrevActiveElement(document.activeElement as HTMLElement);

    defaultPrevented ? setVisible(beforeVisible) : setInnerVisible(newVisible);

    return Promise.resolve({
      isChanged: !defaultPrevented,
      newInnerVisible: newVisible,
    });
  };

  const emitWithEvent = (
    type: DIALOG_EVENT_TYPE,
    trigger?: Nullish<DIALOG_TRIGGER>,
  ) => {
    const event = new CustomEvent(type, trigger!);

    emit(type, event as any);
    setHideTrigger(null);

    return Promise.resolve(event.defaultPrevented);
  };

  const focusContainer = () => {
    containerRef.current?.focus();
  };

  const hide = () => {
    setVisible(false);
  };

  const onBackdropClick = () => {
    setHideTrigger(DIALOG_TRIGGER.BACKDROP);
    hide();
  };

  const show = () => {
    setVisible(true);
  };

  const setVisible = (visible: boolean) => {
    emit('updateVisible', visible);
  };

  useSubscriptions(() => [escKeyEventSubscription], [visible]);

  useEffect(() => {
    if (visible !== innerVisible) {
      beforeChangeVisible(visible, !visible).then(
        ({ isChanged, newInnerVisible }) =>
          isChanged && afterChangeVisible(newInnerVisible),
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible, innerVisible]);

  return {
    state: {
      containerRef,
      duration,
      innerVisible,
      zIndex,
    },

    actions: {
      hide,
      show,
      setHideTrigger,
    },

    refs: {
      containerRef,
    },

    events: {
      onBackdropClick,
    },
  };
}
